<script setup lang="ts">
// `DataExportStep2.vue:136-199` + `:734-773` — the wizard's filter block, rebuilt as a THIN ADAPTER
// over module 08's `ReportFilters`.
//
// Legacy Step 2 was the THIRD re-implementation of report filters (its own column select, its own
// mode select, its own value editor, its own `availableModes`). Nothing of that is ported: this
// file maps the draft's `ExportFilterDraft[]` to 08's `ReportFilter[]` and back, and 08 owns the
// editor, the mode narrowing (`is-export` reads `filter_criteria`, exactly what legacy's
// `availableModes` did at `:734-740`) and the value serialisation.
//
// Two legacy defects disappear as a CONSEQUENCE of the reuse, not as patches:
//   • `:251` passed an out-of-scope `index` from the sort-metric select into `handleColumnChange`,
//     writing `localFilters[undefined]`. The sort control no longer touches filters at all, and no
//     index crosses this boundary — 08 emits the whole list.
//   • `:772` hand-built `"[a"," b]"` for `in`/`not_in`. 08's `normalizeReportFilterInput` emits a
//     REAL array, which is what reaches `buildPayload`.
import { computed } from 'vue'

import type { ExportColumnSelection, ExportFilterDraft } from '../../models/exportJob.types'

import {
  MATCH_MODES,
  ReportFilters,
  dropdownablesFromRows,
  type FilterMatchMode,
  type ReportColumn,
  type ReportFilter,
  type ReportRow,
} from '@/features/reports'
import { useI18n } from '@/shared/i18n'

const props = withDefaults(
  defineProps<{
    columns: readonly ExportColumnSelection[]
    /**
     * Rows to derive filter-value dropdowns from. Legacy reached for a hard-coded report
     * (`workspaceStore.getScheduleByReportId(6)`, `:369-370`) to populate them; the wizard holds no
     * report read, so the caller supplies rows when it has them and 08's editor stays free-text
     * otherwise. No hidden fetch is issued from here.
     */
    rows?: readonly ReportRow[]
  }>(),
  { rows: () => [] },
)
const model = defineModel<ExportFilterDraft[]>({ default: () => [] })
const { t } = useI18n()

const reportColumns = computed<ReportColumn[]>(() =>
  props.columns.map((column) => ({
    field: column.field,
    header: column.header,
    numeric: column.numeric,
    ...(column.filter_criteria === undefined ? {} : { filter_criteria: column.filter_criteria }),
  })),
)

/** Derived from rows the caller already holds — never fetched. Empty rows ⇒ no dropdownables. */
const dropdownables = computed(() =>
  props.rows.length === 0 ? {} : dropdownablesFromRows(props.rows, reportColumns.value),
)

const reportFilters = computed<ReportFilter[]>(() =>
  model.value.map((filter) => ({
    field: filter.column.field,
    match_mode: filter.match_mode.field as FilterMatchMode,
    value: filter.value,
  })),
)

/** 08 → draft. `header`/`numeric` are re-read from the catalog: `buildPayload` ships both. */
function toDraft(filter: ReportFilter): ExportFilterDraft {
  const column = props.columns.find((item) => item.field === filter.field)
  return {
    value: filter.value,
    match_mode: { field: filter.match_mode },
    column: {
      field: filter.field,
      header: column?.header ?? filter.field,
      numeric: Boolean(column?.numeric),
    },
  }
}

function onUpdate(filters: readonly ReportFilter[]): void {
  model.value = filters.map(toDraft)
}
</script>

<template>
  <section class="export-filters grid gap-sm" :aria-label="t('dataExport.data.filters.title')">
    <h3 class="export-filters__title text-sm font-semibold">
      {{ t('dataExport.data.filters.title') }}
    </h3>
    <ReportFilters
      :model-value="reportFilters"
      :columns="reportColumns"
      :dropdownables="dropdownables"
      :match-modes="MATCH_MODES"
      is-export
      variant="standalone"
      :label="t('dataExport.data.filters.label')"
      @update:model-value="onUpdate"
    />
  </section>
</template>
